/// <reference types="google.maps" />

import {MapService, gmAPI} from './Map';
import {createIconLayer, createDeckglOverlay, pickMultipleObjects, positionTransition} from './Deckgl';
import {markerIconMap} from './Icons';
import {isMobile} from '@/lib/ReusableFunctions/User';

/**
 * @typedef {Object} MarkerData
 * @property {string} type - the type of marker to be used
 * @property {string} collection - the collection of marker to be used
 * @property {string} [sourceCollection] - the collection it was added under, which `collection` loses on mobile
 * @property {string} id - the id of the marker to be used
 * @property {string} name - the name of the marker to be used
 * @property {google.maps.LatLng|google.maps.LatLngLiteral} latLng - the latLng of the marker to be used
 * @property {string} icon - the name of the icon to be used for the marker.
 * @property {boolean} clickable - whether the marker is clickable
 * @property {boolean} visible - whether the marker is visible
 * @property {boolean} draggable - whether the marker is draggable
 * Must be a valid icon name from the `Markers.icons` map.
 * @property {Array<{event: string, callback: Function}>} events - the events to be attached to the marker
 *
 * @typedef  {import('@deck.gl/google-maps').GoogleMapsOverlay} GoogleMapsOverlay
 *
 * @typedef {Object} GoogleMarker
 * @property {string} id - the id of the marker to be used
 * @property {string} collection - the collection of marker to be used
 * @property {google.maps.Marker} marker - the google marker
 */

/**
 * @private
 * @type {Map<string, Map<string, MarkerData>>}
 * Each marker collection has a corresponding Map of markers, where the key is the marker ID.
 */
const _markers = new Map();

/**
 * @private
 * @type {Map<string, GoogleMapsOverlay>}
 * Each marker collection has a corresponding GoogleMapsOverlay, which is a deck.gl implementation of
 * a Google Maps OverlayView. Each GoogleMapsOverlay contains an IconLayer, which is a deck.gl layer.
 */
const _markerOverlayViews = new Map();

/**
 * @private
 * @type {Map<string, boolean>}
 * Each marker collection has a corresponding boolean value, which is true if the mouse is hovering over a marker
 */
const _hoveringState = new Map();

/**
 * @private
 * @type {Map<string, string>}
 * Each marker collection may have an ID for a marker that is highlighted
 */
const _highlightState = new Map();

/**
 * @private
 * @type {Map<string, boolean>}
 * Each marker collection has a corresponding boolean value, which is true if a marker is bouncing
 */
const _bounceState = new Map();

/**
 * @private
 * Emphasis: an emphasized marker carries the atlas's `Added-Badge` sprite, and a ping is one bounce of the icon
 * through `getSize`.
 * Keyed by the caller's collection, not the bucket: on mobile they share one, and an empty set would wipe a sibling's.
 * @type {Map<string, Set<string>>} collection → emphasized marker ids
 */
const _emphasis = new Map();
/** @type {Map<string, Map<string, {startedAt: number, scale: number, durationMs: number}>>} collection → id → ping */
const _pings = new Map();
/**
 * @type {Map<string, {iconArr: MarkerData[], visible: boolean, onHover: Function, badged?: object[]}>} collection
 * → what its layer was last drawn from, its hover handler, and the badged array built from them. Animation frames
 * redraw from the same arrays, so deck.gl recomputes sizes only; the handler is made once so a redraw mid-hover
 * keeps its mouseout bookkeeping.
 */
const _layerState = new Map();
/** @type {number|null} the pending animation frame, while a ping is in flight */
let _emphasisFrame = null;
let _lastEmphasisTick = 0;
/** ~30 fps is plenty for a bounce and halves the attribute updates. */
const EMPHASIS_FRAME_MS = 33;
const DEFAULT_EMPHASIS_STYLE = Object.freeze({
  /** A green check badge on each emphasized marker. */
  badge: true,
  badgeSizePx: 16,
  /**
   * Whether icon sizes ease. Off for a consumer whose badges come and go: deck.gl eases per slot, so a badge
   * entering the data would spring every marker after it.
   */
  sizeTransitions: true,
  /** A ping bounces to this multiple and back. */
  pingScale: 1.9,
  pingDurationMs: 900,
});
let _emphasisStyle = {...DEFAULT_EMPHASIS_STYLE};

/**
 * @private
 * @type {GoogleMarker|null}
 */
let _googleMarker = null;
const hideGoogleMarker = () => {
  _googleMarker?.marker?.setMap(null);
  _googleMarker = null;
};

/**
 * Returns the z-index for the given collectionName. GoogleMapOverlay takes a style prop, which
 * can include a zIndex property which allows us to display markers on top of other layers and graphics.
 * @param {string} collectionName The name of the marker collection
 * @return {string} The z-index for the collection
 */
const collectionZIndex = (collectionName) => {
  return {
    'industrialBuildings': '200',
    'industrialProjects': '190',
    'acquisitionProjects': '180',
    'multiFamilyProjects': '170',
    'multiFamilyAcquisitionProjects': '170',
    'dataCenterProjects': '165',
    'competitiveBuildings': '160',
    'competitiveBuildingComps': '150',
    'dataCenterBuildings': '140',
    'multiFamilyBuildings': '130',
  }[collectionName] || '100';
};

const MARKER_HEIGHT = 28; // pixels
let _clickabilityMode = true;

/** @type {number} When a marker is highlighted, it will also scale the size by this amount */
let _markerHighlightScale = 1.25;

const createIcon = async (iconName) => {
  const icon = {...markerIconMap.get(iconName)};
  // the google marker is only used when a marker is selected to be draggable and will therefore always be highlighted
  icon.scaledSize = new gmAPI.Size(icon.size.width * _markerHighlightScale, icon.size.height * _markerHighlightScale);
  icon.size = icon.scaledSize;
  // circular icons should anchor in the center, balloons and compasses should anchor at the bottom
  const bottomOriginTypes = ['Compass-Up', 'Balloon'];
  if (bottomOriginTypes.some((type) => iconName.includes(type))) {
    icon.anchor = new gmAPI.Point(icon.size.width / 2, icon.size.height); // Bottom origin
  } else {
    icon.anchor = new gmAPI.Point(icon.size.width / 2, icon.size.height / 2); // Center origin
  }
  const imgResponse = await fetch(icon.url);
  const imgBlob = await imgResponse.blob();
  const bitmap = await createImageBitmap(imgBlob);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  ctx.drawImage(bitmap, 0, 0);
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  icon.url = canvas.toDataURL();

  return icon;
};

// There is a potential bug with Deck.gl: on mobile devices, if we separate markers into different layers,
// the app crashes even when only rendering 10 markers per layer.
// When all markers are rendered in one layer, the app does not crash even with 10,000+ markers.
const allInOneCollection = isMobile();

const _bucketOf = (collection) => (allInOneCollection ? 'all' : collection);

/** Where a marker really sits: `addMarkers` merges into one bucket on a touch device, `create` never has. */
const _holdingBucket = (collection, id) => (_markers.get(collection)?.has(id) ? collection : _bucketOf(collection));

/**
 * Marker class provides convenience methods for manipulating map markers.
 */
export const Markers = {
  /**
   * Add multiple markers to the map.
   * @param {string} collectionName The name of the marker collection to add
   * @param {MarkerData[]} markers The markers to add
   * @return {void}
   */
  addMarkers: function(collectionName, markers) {
    const colName = allInOneCollection ? 'all' : collectionName;
    const newIds = [];
    for (let i = 0; i < markers.length; i++) {
      if (!markers[i]) continue; // an undecorated collection item maps to undefined markerData
      markers[i].clickable = _clickabilityMode;
      markers[i].visible = false;
      markers[i].draggable = false;
      markers[i].collection = colName;
      markers[i].sourceCollection = collectionName;
      this.addToMarkerMap(colName, markers[i]);
      newIds.push(markers[i].id);
    }
    this.drawIconLayer(colName);

    // Delay the added markers' visibility so they don't display before their position settles
    // (a deck.gl bug workaround); hiding the whole layer instead would blank existing markers.
    setTimeout(() => {
      const stillPresent = newIds.filter((id) => _markers.get(colName)?.has(id));
      if (stillPresent.length) {
        this.setVisible(colName, stillPresent, true);
      }
    }, 100);
  },
  /**
   * Updates markers' data in place (adding any that are missing) with a single layer redraw.
   * Unlike addMarkers there is no hide/re-show cycle: updated markers already have settled
   * positions, so the deck.gl position-flash workaround would only blank the layer for 100ms.
   * @param {string} collectionName
   * @param {MarkerData[]} markers
   * @return {void}
   */
  upsertMarkers: function(collectionName, markers) {
    const colName = allInOneCollection ? 'all' : collectionName;
    for (let i = 0; i < markers.length; i++) {
      if (!markers[i]) continue;
      const existing = _markers.get(colName)?.get(markers[i].id);
      markers[i].clickable = _clickabilityMode;
      markers[i].visible = existing ? existing.visible : true;
      markers[i].draggable = false;
      markers[i].collection = colName;
      markers[i].sourceCollection = collectionName;
      this.addToMarkerMap(colName, markers[i]);
      // The selected marker's google overlay holds its own icon copy (same sync as updateMarker).
      if (markers[i].icon && _googleMarker?.id === markers[i].id) {
        createIcon(markers[i].icon).then((icon) => _googleMarker.marker?.setIcon(icon));
      }
    }
    this.drawIconLayer(colName);
  },
  /**
   * Add a single marker to the map.
   * @param {MarkerData} markerData The marker to add
   * @return {MarkerData} The marker that was added
   */
  create: function(markerData) {
    markerData.clickable = _clickabilityMode;
    markerData.visible = true;
    markerData.draggable = false;

    this.addToMarkerMap(markerData.collection, markerData);
    this.drawIconLayer(markerData.collection);

    return markerData;
  },
  /**
   * Removes a marker from the map.
   * @param {string} collection The collection the marker should be removed from on the canvas.
   * @param {string | symbol} id The IDs of the marker to remove.
   * @return {boolean} true if the marker was removed, false if not removed, or did not exist
   */
  delete: function(collection, id) {
    const colName = allInOneCollection ? 'all' : collection;
    if (!_markers.has(colName)) return false;

    _markers.get(colName).delete(id);
    if (_googleMarker?.id === id && _googleMarker?.collection === colName) {
      hideGoogleMarker();
    }
    this.drawIconLayer(colName);

    return true;
  },
  /**
   * Adds the marker data to _markers.
   * @param {string} collectionName The name of the marker collection to add the marker to
   * @param {MarkerData} markerData The marker data to add
   * @return {void}
   */
  addToMarkerMap: function(collectionName, markerData) {
    // make sure the Map for this type has been created
    if (!_markers.has(collectionName)) {
      _markers.set(collectionName, new Map());
    }

    try {
      if (markerData.latLng?.lat == null || markerData.latLng?.lng == null) {
        throw new Error(`marker latLng must have lat and lng properties: ${JSON.stringify(markerData)}`);
      }

      _markers.get(collectionName).set(markerData.id, markerData);
    } catch (error) {
      console.error('Could not create marker: ', error.message);
    }
  },
  /**
   * Updates a marker in the collection. Only the properties passed in markerData will be updated.
   * @param {*} collection The collection of marker to update
   * @param {*} id The ID of the marker to update
   * @param {*} markerData Marker data to update
   * @return {void}
   */
  updateMarker: async function(collection, id, markerData) {
    const colName = allInOneCollection ? 'all' : collection;
    const existingMarker = this.get(colName, id);
    // Update the marker in the _markers map
    _markers.get(colName).set(id, {...existingMarker, ...markerData});

    // If the change includes an icon, update the icon image for the google marker
    if (markerData.icon && _googleMarker?.id === id) {
      _googleMarker.marker?.setIcon(await createIcon(markerData.icon));
    }

    this.drawIconLayer(colName);
  },
  /**
   * Creates a new GoogleMapsOverlay for the given collectionName and adds it to the map,
   * replacing any existing overlay for that collectionName.
   * @param {string} collectionName The name of the marker collection to add to the overlay
   * @param {boolean} [visible=true] Whether the overlay should be visible
   * @return {void}
   */
  drawIconLayer: function(collectionName, visible = true) {
    const iconArr = [..._markers.get(collectionName).values()];
    const onHover = _layerState.get(collectionName)?.onHover ?? _hoverCallback(collectionName);
    _layerState.set(collectionName, {iconArr, visible, onHover});
    const overlay = _renderIconLayer(collectionName, Date.now());

    const m = MapService.getMap();
    overlay.setMap(m);
    _markerOverlayViews.set(collectionName, overlay);

    // HACK: The WebGLOverlayView.onDraw() method is not called unless some map property is changed, it does not render
    // on the call to setMap(). This is a workaround to force the overlay to render without touching the internals of
    // the deck.gl library or visibly changing the map.
    const c = m.getCenter();
    m.setCenter(new gmAPI.LatLng(c.lat() + 0.000000000001, c.lng()));
  },
  /**
   * Returns the markers at the given screen pixel position. If maxMarkers is provided, returns up to that many markers.
   * @param {google.maps.Point} point The screen pixel position to check for markers
   * @param {number} maxMarkers The maximum number of markers to return
   * @return {Array<MarkerData>} The markers at the given screen pixel position
   */
  getMarkersAtPosition: function({x, y}, maxMarkers = Infinity) {
    const results = [];
    _markerOverlayViews.forEach((overlay) => {
      if (results.length >= maxMarkers) return;
      const selectedMarkers = pickMultipleObjects({
        overlay,
        x,
        y,
        radius: 1,
        depth: maxMarkers,
      });

      selectedMarkers?.forEach((selectedMarker) => {
        if (results.length >= maxMarkers) return;

        // A badge picks as its marker, as on hover; a pick that caught both is one hit.
        const object = selectedMarker.object;
        const marker = object?.isBadge ? object.forMarker : object;
        if (!marker || !marker.visible || !marker.clickable || results.includes(marker)) return;

        results.push(marker);
      });
    });
    return results;
  },
  /**
   * exists returns true if the marker exists
   * @param {string} collection The collection of marker to check.
   * @param {string | symbol} id The ID of the marker to check.
   * @return {boolean} true if the marker exists, false if not
   */
  exists: function(collection, id) {
    const colName = _holdingBucket(collection, id);
    return _markers.has(colName) && _markers.get(colName).has(id);
  },
  /**
   * isDrawn returns true if the marker is on the map right now. Filters and layer toggles hide markers rather than
   * delete them, so `exists` alone says nothing about what the user can see.
   * @param {string} collection The collection of marker to check.
   * @param {string | symbol} id The ID of the marker to check.
   * @return {boolean} true if the marker exists, is visible, and its layer is shown
   */
  isDrawn: function(collection, id) {
    const colName = _holdingBucket(collection, id);
    if (!this.exists(colName, id)) return false;
    return Boolean(_markers.get(colName).get(id).visible) && Boolean(_layerState.get(colName)?.visible);
  },
  /**
   * get an existing marker by collection and id
   * @param {string} collection
   * get an existing marker by collection and id
   * @param {string} id
   * @return {MarkerData} the marker
   */
  get: function(collection, id) {
    const colName = _holdingBucket(collection, id);
    _checkMarkerExists('Cannot get marker', colName, id);
    return _markers.get(colName).get(id);
  },
  /**
   * getByType gets the marker by the marker type and ID.
   * @param {string} type The type of marker to check.
   * @param {string} id The ID of the marker to check.
   * @return {MarkerData|null} returns the marker if it exists, null if it does not
   */
  getByType: function(type, id) {
    let resultMarker = null;
    _markers.forEach((markerMap) => {
      const marker = markerMap.get(id);
      if (marker && marker.type === type) {
        resultMarker = marker;
      }
    });
    return resultMarker;
  },
  /**
   * setPosition sets the position of the marker.
   * @param {string} collection The collection of marker to set the position of.
   * @param {string} id The ID of the marker to set the position of.
   * @param {number} lat latitude of marker to set
   * @param {number} lng longitude of marker to set
   * @throws {ReferenceError} if the marker does not exist
   */
  setPosition: async function(collection, id, lat, lng) {
    await this.updateMarker(collection, id, {latLng: {lat, lng}});
  },
  /**
   * addListener adds a listener to a marker.
   * @param {string} collection The collection of marker on which to add a listener.
   * @param {string} id The ID of the marker on which to add a listener.
   * @param {string} event The event to listen for.
   * @param {function(google.maps.MapMouseEvent): void} callback The callback to call when the event is triggered.
   * @return {void}
   * @throws {ReferenceError} if the marker does not exist
   */
  addListener: async function(collection, id, event, callback) {
    const marker = this.get(collection, id);
    await this.updateMarker(collection, id, {
      events: [...marker.events, {event, callback}],
    });
    // Add the listeners to the deck.gl overlay
    this.drawIconLayer(collection);
  },
  /**
   * removeListener removes a listener from a marker.
   * @param {string} collection The collection of marker on which to remove a listener.
   * @param {string} id The ID of the marker on which to remove a listener.
   * @param {string} event The event to remove.
   * @return {void}
   * @throws {ReferenceError} if the marker does not exist
   */
  removeListener: async function(collection, id, event) {
    const marker = this.get(collection, id);
    await this.updateMarker(collection, id, {
      events: marker.events.filter((e) => e.event !== event),
    });
    // Remove the listeners from the deck.gl overlay
    this.drawIconLayer(collection);
  },
  /**
   * setAllVisible sets the visibility of all markers of a given collection to the given value.
   * Does not remove markers from the map or delete them, it only sets the visibility.
   * @param {string} collection The collection of marker to set the visibility of.
   * @param {boolean} visible true to make all markers visible, false to make all markers invisible
   * @throws {ReferenceError} if the marker collection does not exist
   */
  setAllVisible: function(collection, visible) {
    const colName = allInOneCollection ? 'all' : collection;
    _checkMarkerExists('Cannot set visibility', colName);
    const overlayView = _markerOverlayViews.get(colName);
    if (overlayView) {
      this.drawIconLayer(colName, visible);
    }
  },
  /**
   * setVisible sets the visibility of the markers with the given IDs to the provided value
   * @param {string} collection The collection of marker to set the visibility of.
   * @param {string[]} ids The IDs of the markers to set the visibility of.
   * @param {boolean} visible true to make all markers visible, false to make all markers invisible
   * @throws {ReferenceError} if the marker type does not exist, or if any of the IDs do not exist
   */
  setVisible: function(collection, ids, visible) {
    const colName = allInOneCollection ? 'all' : collection;
    _checkMarkerExists('Cannot set visibility on one or more markers', colName);
    const markers = _markers.get(colName);

    ids.forEach((id) => {
      markers.set(id, {
        ...markers.get(id),
        visible,
        clickable: _clickabilityMode,
      });
      if (_googleMarker?.id === id && _googleMarker?.collection === colName) {
        hideGoogleMarker();
      }
    });

    this.drawIconLayer(colName);
  },
  /**
   * Uses the experimental `_pickable` prop to set pickability of all markers of all types to the given value.
   * @param {boolean} pickable
   */
  setAllPickable: function(pickable = true) {
    _markerOverlayViews.forEach((overlay) => {
      overlay.setProps({
        _pickable: pickable,
      });
    });
  },
  /**
   * setClickable sets the clickability of all markers of all types to the given value. While set to false,
   * all markers will ignore click events, including newly created/displayed markers which
   * did not exist or were not visible when the clickability was set.
   * @param {boolean} clickable true to make all markers clickable, false to make all markers unclickable
   */
  setAllClickable: function(clickable) {
    _clickabilityMode = clickable;
    _markers.forEach((markers) => {
      markers.forEach((marker) => {
        if (!marker) return;
        marker.clickable = clickable;
      });
    });
  },
  /**
   * Either converts a non-draggable marker to be draggable and have an event listener or
   * converts a currently draggable marker into a non-draggable deck.gl marker
   * @param {string} collection the marker collection to search
   * @param {string | symbol} id the marker to find in the given collection and set draggable
   * @param {boolean} isDraggable whether or not the marker should be draggable
   * @param {Function} [dragEndCallback] the callback to call when the marker is dragged.
   */
  setDraggable: async function(collection, id, isDraggable, dragEndCallback) {
    const colName = allInOneCollection ? 'all' : collection;
    if (_googleMarker) {
      this.setVisible(_googleMarker.collection, [_googleMarker.id], true);
      _googleMarker?.marker?.setMap(null);
      _googleMarker = null;
    }

    if (!isDraggable) return;

    const marker = this.get(colName, id);

    this.setVisible(colName, [id], false);

    /** @type {google.maps.Icon} */
    const icon = await createIcon(marker.icon);

    _googleMarker = {
      marker: new gmAPI.Marker({
        position: marker.latLng,
        title: marker.name,
        map: MapService.getMap(),
        draggable: true,
        clickable: _clickabilityMode,
        icon,
      }),
      id: id,
      collection: colName,
    };

    const dragEndEvent = marker.events.find((event) => event.event === 'dragend');
    if (!dragEndEvent) {
      throw new ReferenceError('Cannot set draggable, no dragend event found');
    }
    _googleMarker.marker.addListener('dragend', async (event) => {
      const latLng = event.latLng.toJSON();
      dragEndEvent.callback(latLng); // info window helper callback
      await this.updateMarker(collection, id, {latLng}); // local markers update
      dragEndCallback?.(latLng); // info window component callback
    });
  },
  /**
   * clearOverlays removes the existing marker overlays from the map when unmounted
   */
  clearOverlays: function() {
    _markerOverlayViews.forEach((overlay) => {
      overlay.setMap(null);
    });
    _markers.clear();
    _markerOverlayViews.clear();
    // A ping outliving its layer is never redrawn, so it never finishes and its animation frame re-arms forever.
    _layerState.clear();
    this.clearEmphasis();
  },
  /**
   * A pure deck.gl implementation of a bounce animation to eliminate the need for converting to a google marker
   * @param {string} collection the collection of marker to animate
   * @param {string} id the id of the marker to animate
   */
  playBounceAnimation: function(collection, id) {
    const colName = allInOneCollection ? 'all' : collection;
    _checkMarkerExists('Cannot animate marker bounce', colName, id);
    // The bounce is a ~2s deck.gl/WebGL animation. Playwright's `animations: 'disabled'` only freezes CSS/Web
    // animations, not WebGL, and its stability wait can't hold a continuously-moving canvas still — so a marker
    // captured mid-bounce yields flaky screenshots.
    if (window['PLAYWRIGHT_FIREBASE_CONFIG']?.emulators) {
      this.drawIconLayer(colName);
      return;
    }
    const latLng = this.get(colName, id).latLng;
    const pixel = MapService.latLngToScreenPixel(latLng);
    const newLatLng = MapService.screenPixelToLatLng(pixel.x, pixel.y - MARKER_HEIGHT * 0.5);
    _bounceState.set(colName, true);
    this.drawIconLayer(colName);
    this.setPosition(colName, id, newLatLng.lat(), newLatLng.lng()); // bounce up
    setTimeout(() => { // bounce down
      const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
      const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
      this.setPosition(colName, id, lat, lng);
      setTimeout(() => { // bounce up
        this.setPosition(colName, id, newLatLng.lat(), newLatLng.lng());
        setTimeout(() => { // bounce down
          this.setPosition(colName, id, lat, lng);
          setTimeout(() => { // turn off bounce state
            _bounceState.set(colName, false);
            this.drawIconLayer(colName);
          }, 400);
        }, 400);
      }, 400);
    }, 400);
  },
  /**
   * setHighlight lightens the marker and increases its size to indicate it is highlighted
   * @param {string} collection the collection of marker to highlight
   * @param {string} id the id of the marker to highlight
   * @param {boolean} state true to highlight the marker, false to remove the highlight
   */
  setHighlight: function(collection, id, state) {
    const colName = allInOneCollection ? 'all' : collection;
    if (!this.exists(colName, id)) {
      // If the ID is a symbol, converting it to a string will throw an error
      if (typeof id === 'string') {
        console.warn(`Cannot set highlight, marker with collection ${colName} and id ${id} does not exist`);
      }
      return;
    }
    // only force a redraw when the highlight state actually changes to avoid [this issue](https://github.com/visgl/deck.gl/issues/8936)
    if (state && !(_highlightState.get(colName) === id)) {
      _highlightState.set(colName, id);
      this.drawIconLayer(colName);
    } else if (!state && _highlightState.get(colName)) {
      _highlightState.delete(colName);
      this.drawIconLayer(colName);
    }
  },
  setHighlightScale: function(scale) {
    _markerHighlightScale = scale;
  },
  /**
   * Adjusts how emphasis is drawn — `{badge: false}` drops the check badge (pings still play). Merged over the
   * defaults; takes effect on the next frame.
   * @param {Partial<typeof _emphasisStyle>} style
   */
  configureEmphasis: function(style) {
    _emphasisStyle = {..._emphasisStyle, ...style};
    _dropBadgeCache();
    _redrawEmphasis();
  },
  /**
   * Emphasizes exactly these markers of the collection; ids not (yet) on the map are ignored until they are drawn.
   * @param {string} collection
   * @param {string[]} ids
   */
  setEmphasis: function(collection, ids) {
    const colName = _bucketOf(collection);
    if (ids.length) {
      _emphasis.set(collection, new Set(ids));
    } else {
      _emphasis.delete(collection);
    }
    _dropBadgeCache(colName);
    _redrawEmphasis(colName);
  },
  /**
   * One bounce of the marker — "here it is".
   * @param {string} collection
   * @param {string} id
   * @param {{scale?: number, durationMs?: number}} [options] the bounce's peak (a multiple of the marker's size)
   *   and length; the style's by default
   * @return {boolean} whether the marker exists
   */
  ping: function(collection, id, {scale = _emphasisStyle.pingScale, durationMs = _emphasisStyle.pingDurationMs} = {}) {
    const colName = allInOneCollection ? 'all' : collection;
    if (!this.exists(colName, id)) return false;
    if (!_pings.has(colName)) {
      _pings.set(colName, new Map());
    }
    _pings.get(colName).set(id, {startedAt: Date.now(), scale, durationMs});
    _redrawEmphasis(colName);
    return true;
  },
  /** Drops every badge and ping, stops animating, and hands the style back to the next view's defaults. */
  clearEmphasis: function() {
    const affected = new Set([...[..._emphasis.keys()].map(_bucketOf), ..._pings.keys()]);
    _emphasis.clear();
    _pings.clear();
    _emphasisStyle = {...DEFAULT_EMPHASIS_STYLE};
    if (_emphasisFrame != null) {
      cancelAnimationFrame(_emphasisFrame);
      _emphasisFrame = null;
    }
    affected.forEach((colName) => {
      _dropBadgeCache(colName);
      _redrawEmphasis(colName);
    });
  },
  areClickable: function() {
    return _clickabilityMode;
  },
  getMarkers: function() {
    return _markers;
  },
  getMarkerOverlayViews: function() {
    return _markerOverlayViews;
  },
};

/**
 * The hover handler for a collection's IconLayer.
 * @param {string} collectionName
 * @return {Function}
 */
const _hoverCallback = (collectionName) => {
  // Keep track of mouseout events to call when the mouse leaves a marker
  const mouseoutEvents = new Map();
  return (/** @type {import('@deck.gl/core').PickingInfo} */{object}) => {
    if (!_clickabilityMode) return;
    // Hovering a marker's badge is hovering the marker.
    const marker = object?.isBadge ? object.forMarker : object;

    if (marker?.visible && marker?.clickable) {
      _hoveringState.set(collectionName, Boolean(marker));
      MapService.setCursor('pointer');

      // Handle hover events
      marker.events?.forEach((event) => {
        if (event.event === 'mouseover') {
          event.callback(marker);
        } else if (event.event === 'mouseout') {
          mouseoutEvents.set(event.event, event.callback);
        }
      });
    } else {
      _hoveringState.set(collectionName, false);
      MapService.setCursor(null);
      mouseoutEvents.forEach((callback) => callback(marker));
      mouseoutEvents.clear();
    }
  };
};

/**
 * How much each marker of a collection is scaled at a moment in time — a ping's bounce, or 1. Pings that have
 * finished are dropped here, on the frame that finds them done.
 * @param {string} colName
 * @param {number} now
 * @return {{scaleFor: (id: string) => number, animating: boolean}}
 */
const _emphasisScale = (colName, now) => {
  const pings = _pings.get(colName);
  pings?.forEach(({startedAt, durationMs}, id) => {
    if (now - startedAt >= durationMs) {
      pings.delete(id);
    }
  });
  if (pings && !pings.size) {
    _pings.delete(colName);
  }
  return {
    animating: Boolean(pings?.size),
    scaleFor: (id) => {
      const ping = pings?.get(id);
      return ping ? 1 + (ping.scale - 1) * Math.sin(Math.PI * ((now - ping.startedAt) / ping.durationMs)) : 1;
    },
  };
};

/**
 * Builds the collection's IconLayer from what `drawIconLayer` last recorded and puts it on its overlay, creating
 * the overlay the first time. Called by `drawIconLayer` and by every animation frame.
 * @param {string} collectionName
 * @param {number} now
 * @return {GoogleMapsOverlay}
 */
const _renderIconLayer = (collectionName, now) => {
  const state = _layerState.get(collectionName);
  const {iconArr, visible, onHover} = state;
  const highlightedId = _highlightState.get(collectionName);
  const {scaleFor, animating} = _emphasisScale(collectionName, now);
  /** How big a marker is drawn, as a multiple of normal: the subject highlight times a ping's bounce. */
  const factorFor = (marker) => (marker.id === highlightedId ? _markerHighlightScale : 1) * scaleFor(marker.id);
  if (!state.badged) {
    state.badged = _withBadges(collectionName, iconArr);
  }
  const data = state.badged;
  const highlightedObjectIndex = data.findIndex((datum) => !datum.isBadge && highlightedId === datum.id);
  // While a ping plays, every frame is a new size; at rest only the highlight decides it.
  const sizeTrigger = [highlightedObjectIndex, animating ? now : 0];

  /** @type {import('@deck.gl/layers').IconLayerProps} */
  const options = {
    id: `${collectionName}IconLayer`,
    data,
    pickable: true,
    getSize: (datum) =>
      (datum.isBadge ? _emphasisStyle.badgeSizePx : MARKER_HEIGHT) * factorFor(datum.forMarker ?? datum),
    onHover,
    visible,
    highlightedObjectIndex,
    updateTriggers: {getSize: sizeTrigger},
  };
  // Only a badge is drawn off its point; without any, the layer keeps deck.gl's constant offset.
  if (data !== iconArr) {
    options.getPixelOffset = (datum) =>
      (datum.isBadge ? _badgeOffset(datum.forMarker).map((px) => px * factorFor(datum.forMarker)) : [0, 0]);
    options.updateTriggers.getPixelOffset = sizeTrigger;
  }
  // No size spring while a ping plays — the bounce supplies its own easing — or when easing is turned off.
  if (animating || !_emphasisStyle.sizeTransitions) {
    options.transitions = {getSize: undefined};
  }

  if (_bounceState.get(collectionName)) {
    options.transitions = {...options.transitions, getPosition: positionTransition};
  }

  const layers = [createIconLayer(options)];
  if (_markerOverlayViews.has(collectionName)) {
    const overlay = _markerOverlayViews.get(collectionName);
    overlay.setProps({layers});
    return overlay;
  }
  return createDeckglOverlay({
    layers,
    style: {
      zIndex: collectionZIndex(collectionName),
    },
  });
};

/**
 * Where a badge sits relative to its marker's point: balloons and compasses stand on the point, so up near the
 * top-right shoulder; circular icons are centered on it, so toward the top-right edge.
 * @param {MarkerData} marker
 * @return {[number, number]} pixels right and down
 */
const _badgeOffset = (marker) => {
  const standsOnPoint = ['Balloon', 'Compass-Up'].some((type) => marker.icon?.includes(type));
  return standsOnPoint ? [MARKER_HEIGHT * 0.32, -MARKER_HEIGHT * 0.82] : [MARKER_HEIGHT * 0.32, -MARKER_HEIGHT * 0.32];
};

const _emphasizedIds = (colName) => {
  const ids = new Set();
  _emphasis.forEach((set, collection) => {
    if (_bucketOf(collection) === colName) {
      set.forEach((id) => ids.add(id));
    }
  });
  return ids;
};

/**
 * The layer's data: every marker, and right after each emphasized, visible one its badge datum. Drawn in data
 * order, so the badge sits over its own marker and under any nearer one.
 * @param {string} colName
 * @param {MarkerData[]} iconArr
 * @return {object[]}
 */
const _withBadges = (colName, iconArr) => {
  const ids = _emphasizedIds(colName);
  if (!_emphasisStyle.badge || !ids.size) return iconArr;
  const data = [];
  iconArr.forEach((marker) => {
    data.push(marker);
    if (marker.visible && ids.has(marker.id)) {
      data.push({
        isBadge: true,
        forMarker: marker,
        id: `${String(marker.id)}#badge`,
        icon: 'Added-Badge',
        latLng: marker.latLng,
        visible: true,
        clickable: false,
      });
    }
  });
  return data;
};

const _emphasisAnimating = () => [..._pings.values()].some((pings) => pings.size);

const _emphasisTick = () => {
  _emphasisFrame = null;
  const now = Date.now();
  if (now - _lastEmphasisTick >= EMPHASIS_FRAME_MS) {
    _lastEmphasisTick = now;
    new Set([...[..._emphasis.keys()].map(_bucketOf), ..._pings.keys()]).forEach((colName) => {
      if (_markerOverlayViews.has(colName) && _layerState.has(colName)) {
        _renderIconLayer(colName, now);
      }
    });
  }
  _scheduleEmphasisFrame();
};

const _scheduleEmphasisFrame = () => {
  if (_emphasisFrame != null || !_emphasisAnimating() || typeof requestAnimationFrame !== 'function') return;
  _emphasisFrame = requestAnimationFrame(_emphasisTick);
};

/** A ping only resizes what is already drawn, so it deliberately leaves the badged array alone. */
const _dropBadgeCache = (colName) => {
  const names = colName ? [colName] : [..._layerState.keys()];
  names.forEach((name) => {
    const state = _layerState.get(name);
    if (state) {
      state.badged = undefined;
    }
  });
};

/**
 * Redraws one collection (or every drawn one) from its recorded data, so a change in emphasis shows at once.
 * @param {string} [colName]
 */
const _redrawEmphasis = (colName) => {
  const names = colName ? [colName] : [..._markerOverlayViews.keys()];
  const now = Date.now();
  names.forEach((name) => {
    if (_markerOverlayViews.has(name) && _layerState.has(name)) {
      _renderIconLayer(name, now);
    }
  });
  _scheduleEmphasisFrame();
};

/**
 * @param {string} errPrefix Prefix for the error message if the check fails.
 * @param {string} collection The collection of marker to check.
 * @param {string} [id] (optional) The ID of the marker to check.
 * @throws {ReferenceError} if the marker collection does not exist, or if the ID does not exist
 */
function _checkMarkerExists(errPrefix, collection, id) {
  if (!_markers.has(collection)) {
    throw new ReferenceError(`${errPrefix}, marker collection ${collection} does not exist`);
  }
  if (id !== undefined && !_markers.get(collection).has(id)) {
    throw new ReferenceError(`${errPrefix}, marker with collection ${collection} and id ${id} does not exist`);
  }
}
